import axios from 'axios';
import type { AxiosInstance, AxiosError } from 'axios';
import type {
    Product,
    PaginatedResponse,
    StoreOrder,
    OrderFormData,
    CartItem
} from '../types';

// Create axios instance - use relative URL for Vite proxy
const api: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api',
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 15000,
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.response) {
            const status = error.response.status;

            if (status === 404) {
                console.error('Resource not found');
            } else if (status === 500) {
                console.error('Server error');
            }
        } else if (error.request) {
            console.error('Network error - no response received');
        }

        return Promise.reject(error);
    }
);

// Products API
export const productsApi = {
    getAll: async (params?: {
        page?: number;
        limit?: number;
        category?: string;
        search?: string;
    }): Promise<PaginatedResponse<Product>> => {
        const response = await api.get('/store/products', { params });
        return response.data;
    },

    getById: async (id: string): Promise<Product> => {
        const response = await api.get(`/store/products/${id}`);
        return response.data;
    },

    getBySlug: async (categorySlug: string, productSlug: string): Promise<Product> => {
        const response = await api.get(`/store/products/by-slug/${categorySlug}/${productSlug}`);
        return response.data;
    },

    getCategories: async (): Promise<{ name: string; count: number }[]> => {
        const response = await api.get('/store/products/categories');
        return response.data;
    },
};

// Settings API (Public)
export interface StoreSettings {
    storeName: string;
    primaryColor: string;
    freeShippingEnabled: boolean;
    freeShippingThreshold: number;
    deliveryFee: number;
    codEnabled: boolean;
    codLabel: string;
    deliveryDays: string;
    whatsappNumber: string | null;
    phoneNumber: string | null;
    topBannerEnabled: boolean;
    topBannerText: string;
    // Mobile Settings
    mobileNavEnabled: boolean;
    mobileNavStyle: string;
}

export const settingsApi = {
    get: async (): Promise<StoreSettings> => {
        const response = await api.get('/store/settings');
        return response.data;
    },
};

// Wilayas API (Public)
export interface Wilaya {
    id: string;
    code: number;
    name: string;
    nameAr: string | null;
    priceHome: number;
    priceDesk: number;
    isActive: boolean;
    deliveryDays: string;
}

export const wilayasApi = {
    getActive: async (): Promise<Wilaya[]> => {
        const response = await api.get('/store/wilayas');
        return response.data;
    },
};

// Orders API
export const ordersApi = {
    create: async (data: {
        customer: OrderFormData;
        items: CartItem[];
        totalAmount: number;
    }): Promise<StoreOrder> => {
        const response = await api.post('/store/orders', data);
        return response.data;
    },

    getByOrderNumber: async (orderNumber: string): Promise<StoreOrder> => {
        const response = await api.get(`/store/orders/${orderNumber}`);
        return response.data;
    },
};

// Live Cart API
export interface LiveCartItem {
    productId: string;
    productName: string;
    quantity: number;
    price: number;
    imageUrl?: string;
}

export interface LiveCart {
    id: string;
    sessionId: string;
    phoneNumber?: string;
    customerName?: string;
    wilaya?: string;
    wilayaCode?: number;
    commune?: string;
    status: string;
    totalAmount: number;
    shippingFee?: number;
    items: LiveCartItem[];
    lastActivityAt: string;
    phoneEnteredAt?: string;
    createdAt: string;
}

export const liveCartApi = {
    createOrUpdate: async (data: {
        sessionId: string;
        items: LiveCartItem[];
        totalAmount: number;
    }): Promise<LiveCart> => {
        const response = await api.post('/store/live-cart', data);
        return response.data;
    },

    get: async (sessionId: string): Promise<LiveCart | null> => {
        try {
            const response = await api.get(`/store/live-cart/${sessionId}`);
            return response.data;
        } catch {
            return null;
        }
    },

    updatePhone: async (sessionId: string, phoneNumber: string): Promise<LiveCart> => {
        const response = await api.patch(`/store/live-cart/${sessionId}/phone`, { phoneNumber });
        return response.data;
    },

    updateDetails: async (sessionId: string, data: {
        customerName?: string;
        wilaya?: string;
        wilayaCode?: number;
        commune?: string;
        shippingFee?: number;
        totalAmount?: number;
    }): Promise<LiveCart> => {
        const response = await api.patch(`/store/live-cart/${sessionId}/details`, data);
        return response.data;
    },

    convert: async (sessionId: string, orderData: {
        customer: OrderFormData;
        items: CartItem[];
        totalAmount: number;
    }): Promise<StoreOrder> => {
        const response = await api.post(`/store/live-cart/${sessionId}/convert`, orderData);
        return response.data;
    },
};

// Session ID helper - generates or retrieves a persistent session ID
export const getSessionId = (): string => {
    const STORAGE_KEY = 'marchego_session_id';
    let sessionId = localStorage.getItem(STORAGE_KEY);

    if (!sessionId) {
        sessionId = `sess_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 11)}`;
        localStorage.setItem(STORAGE_KEY, sessionId);
    }

    return sessionId;
};

// Helper function for error messages
export const getErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        return error.response?.data?.message || error.message || 'An error occurred';
    }

    if (error instanceof Error) {
        return error.message;
    }

    return 'An unexpected error occurred';
};

// Reviews API
import type { ProductReview, ReviewStats } from '../types';

export const reviewsApi = {
    getByProduct: async (
        productId: string,
        page = 1,
        limit = 10
    ): Promise<PaginatedResponse<ProductReview>> => {
        const response = await api.get(`/store/products/${productId}/reviews`, {
            params: { page, limit },
        });
        return response.data;
    },

    create: async (
        productId: string,
        data: { customerName: string; rating: number; title?: string; comment: string }
    ): Promise<ProductReview> => {
        const response = await api.post(`/store/products/${productId}/reviews`, data);
        return response.data;
    },

    getStats: async (productId: string): Promise<ReviewStats> => {
        const response = await api.get(`/store/products/${productId}/reviews/stats`);
        return response.data;
    },
};

export default api;
